import type {VideoData} from '@Libraries/add-media-library';
import {ElementDataType} from '@Libraries/add-media-library';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {repoURL} from '@Libraries/s3-library';
import {getWriteBucket} from '@Utils/s3.util';
import {pollableJob} from '@Libraries/pollable-job.library';
import {PollError, PollFailError, PollTimeoutError} from '@Libraries/pollable-job.library.types';
import type {PollEnvelope, PollKickoffAck, PollTransportParams} from '@Libraries/pollable-job.library.types';
import {deleteGridItems} from '@Components/base-grid';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {ColorTypes} from '@Components/controlled-list/components/controlled-list-item-type-7/controlled-list-item-type-7';
import {openConfirmDeleteModal} from '@Modals/confirm-delete-modal/confirm-delete-modal.types';

const AI_VIDEO_THUMB_FOLDER = 'aivideos/thumbs';

export type AiVideoThumbExtension = 'jpg' | 'mp4';

export const getAiVideoThumbUrl = (hashedFilename: string, ext: AiVideoThumbExtension): string => {
  return repoURL(`${AI_VIDEO_THUMB_FOLDER}/${hashedFilename}.${ext}`, getWriteBucket());
};
export enum AiVideoGenerationErrorCode {
  INSUFFICIENT_CREDITS = 'INSUFFICIENT_CREDITS',
  CONTENT_BLOCKED = 'CONTENT_BLOCKED',
  BUSY = 'BUSY',
  INTERNAL = 'INTERNAL',
}

export const getAiVideoGenerationErrorMessage = (code: string | undefined): string => {
  if (code === AiVideoGenerationErrorCode.CONTENT_BLOCKED) {
    return window.i18next.t('pmwjs_ai_video_error_content_blocked');
  }
  if (code === AiVideoGenerationErrorCode.BUSY) {
    return window.i18next.t('pmwjs_ai_video_error_busy');
  }
  return window.i18next.t('pmwjs_unexpected_error_occurred');
};

export const DEFAULT_VIDEO_EXTENSION = 'webm';

export enum AiVideoReferenceImageType {
  USER_IMAGE = 'USER_IMAGE',
  AI_ATTACHMENT = 'AI_ATTACHMENT',
}

export interface AiVideoReferenceImage {
  hashedFilename: string;
  type: AiVideoReferenceImageType;
}

export interface GenerateAiVideoParams {
  prompt: string;
  aspectRatio: string;
  quality: string;
  generateAudio: boolean;
  cameraAngle: string;
  textSetting?: string;
  referenceImages?: AiVideoReferenceImage[];
}

export interface AiVideoPromptData {
  idPrompt: number | null;
  text: string;
  aspect_ratio: string | null;
  quality: string | null;
  has_audio: boolean;
  camera_angle: string | null;
  text_setting: string | null;
}

export interface AiVideoVideoProps {
  src: string;
  ext: string;
  width: number;
  height: number;
  duration: number;
  url: string;
  thumbUrl: string;
}

export interface AiVideoBackendData {
  id: number;
  prompt: AiVideoPromptData;
  hashedFilename: string;
  addedToDesign: boolean;
  videoProps: AiVideoVideoProps;
  uploaderId: number | null;
  uploaderName: string | null;
  generatedOn: string | null;
}

export class AiVideoGenerationError extends Error {
  public readonly code?: string;

  constructor(code?: string) {
    super(code);
    this.name = 'AiVideoGenerationError';
    this.code = code;
  }
}

const AI_VIDEO_GENERATION_POLL_TIMEOUT_MS = 1_200_000;

interface AiVideoGenerationDoneData {
  generatedVideo: AiVideoBackendData;
}

const initiateAiVideoGenerationV2 = async (params: GenerateAiVideoParams): Promise<PollKickoffAck> => {
  return (await window.PMW.writeLocal('aivideo/generateVideoV2', params)) as PollKickoffAck;
};

const checkAiVideoGenerationStatusV2 = async (ack: PollKickoffAck, transportParams: PollTransportParams): Promise<PollEnvelope<AiVideoGenerationDoneData>> => {
  return (await window.PMW.readLocal('aivideo/getVideoGenerationStatusV2', {
    pollingKey: ack.pollingKey,
    ...transportParams,
  })) as PollEnvelope<AiVideoGenerationDoneData>;
};

export const generateAiVideo = async (params: GenerateAiVideoParams): Promise<AiVideoBackendData> => {
  trackPosterBuilderGA4Events(GA4EventName.AI_VIDEO_GENERATE);
  try {
    const data = await pollableJob<AiVideoGenerationDoneData>({
      kickoff: () => initiateAiVideoGenerationV2(params),
      status: checkAiVideoGenerationStatusV2,
      timeoutMs: AI_VIDEO_GENERATION_POLL_TIMEOUT_MS,
    });
    return data.generatedVideo;
  } catch (e) {
    if (e instanceof PollFailError || e instanceof PollError) {
      throw new AiVideoGenerationError(e.code);
    }
    if (e instanceof PollTimeoutError) {
      throw new Error('AI video generation timed out without producing a result', {cause: e});
    }
    throw e;
  }
};

interface UploadGeneratedVideoResponse {
  hashedFilename: string;
  extension: string;
  duration: number;
  frameRate: number;
  source: string;
  hasTransparency: boolean;
  width: number;
  height: number;
}

export const uploadGeneratedAiVideo = async (aiGeneratedVideoId: number): Promise<VideoData> => {
  trackPosterBuilderGA4Events(GA4EventName.AI_VIDEO_ADDED);
  const response = (await window.PMW.writeLocal('aivideo/uploadGeneratedVideo', {
    aiGeneratedVideoId,
  })) as UploadGeneratedVideoResponse;

  return {
    type: ElementDataType.VIDEO,
    hashedFilename: response.hashedFilename,
    extension: response.extension,
    duration: response.duration,
    frameRate: response.frameRate,
    source: response.source,
    hasTransparency: response.hasTransparency,
    width: response.width,
    height: response.height,
  } as VideoData;
};

export const getGeneratedAiVideos = async (limit = 20, offset = 0): Promise<AiVideoBackendData[]> => {
  return (await window.PMW.readLocal('aivideo/getAiGeneratedVideos', {limit, offset})) as AiVideoBackendData[];
};

export const deleteGeneratedAiVideo = async (aiGeneratedVideoId: number): Promise<void> => {
  await window.PMW.writeLocal('aivideo/deleteGeneratedVideo', {aiGeneratedVideoId});
};

export interface GeneratedVideoMenuCallbacks {
  onFullscreenPreview: () => void;
  onCopyPrompt: () => void;
  onDelete: () => void;
  isPromptCopied?: boolean;
}

export const getGeneratedVideoMenuItems = ({onFullscreenPreview, onCopyPrompt, onDelete, isPromptCopied = false}: GeneratedVideoMenuCallbacks): ControlledListItemType[] => {
  return [
    {
      id: 'fullscreen-preview',
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      text: window.i18next.t('pmwjs_fullscreen_preview'),
      icon: 'icon-expand',
      isSelected: false,
      className: 'radius-8',
      onClick: onFullscreenPreview,
    },
    {
      id: 'copy-prompt',
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      text: isPromptCopied ? window.i18next.t('pmwjs_prompt_copied') : window.i18next.t('pmwjs_copy_prompt'),
      icon: isPromptCopied ? 'icon-check' : 'icon-copy',
      isSelected: false,
      className: 'radius-8',
      onClick: onCopyPrompt,
    },
    {
      id: 'delete',
      type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
      text: window.i18next.t('pmwjs_delete'),
      icon: 'icon-delete',
      isSelected: false,
      className: 'radius-8',
      color: ColorTypes.danger,
      onClick: onDelete,
    },
  ];
};

export const confirmAndDeleteGeneratedAiVideo = (video: AiVideoBackendData, gridId: string, gridSelectionGroupId: string): void => {
  openConfirmDeleteModal({
    title: window.i18next.t('pmwjs_delete_video'),
    text: window.i18next.t('pmwjs_delete_generated_video_confirmation'),
    showBtnDeletingState: true,
    onDeleteConfirmation: async (): Promise<void> => {
      await deleteGeneratedAiVideo(video.id);
      window.PMW.redux.store.dispatch(
        deleteGridItems({
          gridId,
          gridSelectionGroupId,
          itemIdsToDelete: [`${video.id}`],
        })
      );
    },
  });
};

export const showAiVideoUpsellDialog = async (): Promise<void> => {
  await window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_AI_VIDEO);
};
